import {
  DrawerContent,
  DrawerData,
  DrawerDataHeading,
  DrawerDataRow,
  ExternalLink,
  OutflowContentBox,
  OutflowChipsRow,
  OutflowDivider,
  OutflowLinkChip,
  OutflowMetaLabel,
  OutflowMetaRow,
  OutflowMetaValue,
  OutflowSectionCard,
  OutflowSectionTitle,
  OutflowTitle,
  ReconcilePrimaryButton,
  StyledTransactionInfo,
} from "./styles"
import {
  Invoice,
  P2PDetails,
  Transaction,
} from "../../models/transactions.model"
import dayjs from "dayjs"
import { useState } from "react"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import {
  TransactionType,
  allowedOnlyBankAccountsRoles,
} from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { useAppSelector } from "../../app/hooks"
import { formatPriceWithRupeeSymbol } from "../../utils/price-formatter"
import {
  getTransactionDateDisplayValue,
  getTransactionTypeDisplayValue,
  shouldShowReconcileButton,
} from "../../utils/transactionHelpers"
import { ExternalLinkIcon } from "./ExternalLinkIcon"
import ReconciliationDrawer from "../ReconciliaionDrawer"

type TransactionInfoProps = {
  transactionData: Transaction
}

const TransactionInfo = ({ transactionData }: TransactionInfoProps) => {
  const { role } = useAppSelector(selectLogin)

  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false)

  const handleReconcileDialogOpen = () => {
    setReconcileDialogOpen(true)
  }

  const handleReconcileDialogClose = () => {
    setReconcileDialogOpen(false)
  }

  return (
    <StyledTransactionInfo>
      <DrawerContent>
        <DrawerDataRow>
          <DrawerDataHeading>ID:</DrawerDataHeading>
          <DrawerData>{transactionData.id}</DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Date:</DrawerDataHeading>
          <DrawerData>
            {transactionData.date
              ? getTransactionDateDisplayValue(transactionData.date)
              : "--"}
          </DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Description:</DrawerDataHeading>
          <DrawerData>{transactionData.description || "--"}</DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Amount:</DrawerDataHeading>
          <DrawerData>
            {formatPriceWithRupeeSymbol(transactionData.amount)}
          </DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Type:</DrawerDataHeading>
          <DrawerData>
            {getTransactionTypeDisplayValue(
              transactionData.type as TransactionType,
            )}
          </DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Reference No:</DrawerDataHeading>
          <DrawerData>{transactionData.referenceNo}</DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Value Date:</DrawerDataHeading>
          <DrawerData>
            {transactionData.valueDate
              ? dayjs
                  .utc(transactionData.valueDate)
                  .utcOffset("+05:30")
                  .format("DD/MM/YYYY")
              : "--"}
          </DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Branch:</DrawerDataHeading>
          <DrawerData>{transactionData.branch || "--"}</DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Balance:</DrawerDataHeading>
          <DrawerData>
            {transactionData.balance
              ? formatPriceWithRupeeSymbol(transactionData.balance)
              : "--"}
          </DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Marked:</DrawerDataHeading>
          <DrawerData>{transactionData.isMarked ? "Yes" : "No"}</DrawerData>
        </DrawerDataRow>
        {allowedOnlyBankAccountsRoles.includes(role) &&
        transactionData?.settlements?.invoices?.length ? (
          <DrawerDataRow>
            <DrawerDataHeading>Invoice Number:</DrawerDataHeading>
            <DrawerData>
              {transactionData?.settlements?.invoices?.map(
                (invoice: Invoice) => {
                  return (
                    <ExternalLink
                      onClick={() => {
                        window.open(invoice.invoiceURL)
                      }}
                    >
                      {invoice.invoiceNumber}
                      <ExternalLinkIcon />
                    </ExternalLink>
                  )
                },
              )}
            </DrawerData>
          </DrawerDataRow>
        ) : null}
        {allowedOnlyBankAccountsRoles.includes(role) &&
        transactionData?.settlements?.payments?.length ? (
          <DrawerDataRow>
            <DrawerDataHeading>Payment Id:</DrawerDataHeading>
            <DrawerData>
              {transactionData?.settlements?.payments?.map(
                (payment: { payment_url: string; payment_number: string }) => {
                  return (
                    <ExternalLink
                      onClick={() => {
                        window.open(payment.payment_url)
                      }}
                    >
                      {payment.payment_number}
                      <ExternalLinkIcon />
                    </ExternalLink>
                  )
                },
              )}
            </DrawerData>
          </DrawerDataRow>
        ) : null}
        <DrawerDataRow>
          <DrawerDataHeading>Payee:</DrawerDataHeading>
          <DrawerData>{transactionData.payee ?? "--"}</DrawerData>
        </DrawerDataRow>
        <DrawerDataRow>
          <DrawerDataHeading>Bank Name:</DrawerDataHeading>
          <DrawerData>{transactionData.accountName}</DrawerData>
        </DrawerDataRow>
        {transactionData?.p2pDetails?.map((p2p: P2PDetails) => {
          return (
            <OutflowContentBox>
              <OutflowTitle>Outflow Content</OutflowTitle>

              <OutflowMetaRow>
                <OutflowMetaLabel>Project:</OutflowMetaLabel>
                <OutflowMetaValue>
                  {p2p.project
                    ? `${p2p.project.name} (${p2p.project.id})`
                    : "--"}
                </OutflowMetaValue>
              </OutflowMetaRow>

              <OutflowMetaRow>
                <OutflowMetaLabel>Vendor:</OutflowMetaLabel>
                <OutflowMetaValue>{p2p.vendorName || "--"}</OutflowMetaValue>
              </OutflowMetaRow>

              <OutflowDivider />

              {p2p.purchaseOrders.length > 0 && (
                <OutflowSectionCard>
                  <OutflowSectionTitle>
                    Purchase Orders ({p2p.purchaseOrders.length})
                  </OutflowSectionTitle>
                  <OutflowChipsRow>
                    {p2p.purchaseOrders.map((po) => (
                      <OutflowLinkChip
                        key={po.id}
                        onClick={() => po.p2pLink && window.open(po.p2pLink)}
                        isClickable={Boolean(po.p2pLink)}
                      >
                        {po.p2pLink ? <ExternalLinkIcon /> : ""}
                        {po.id}
                      </OutflowLinkChip>
                    ))}
                  </OutflowChipsRow>
                </OutflowSectionCard>
              )}

              {p2p.invoices.length > 0 && (
                <OutflowSectionCard>
                  <OutflowSectionTitle>
                    Invoices ({p2p.invoices.length})
                  </OutflowSectionTitle>
                  <OutflowChipsRow>
                    {p2p.invoices.map((inv) => (
                      <OutflowLinkChip
                        key={inv.number}
                        onClick={() => inv.pdfLink && window.open(inv.pdfLink)}
                        isClickable={Boolean(inv.pdfLink)}
                      >
                        {inv.pdfLink ? <ExternalLinkIcon /> : ""}
                        {inv.number}
                      </OutflowLinkChip>
                    ))}
                  </OutflowChipsRow>
                </OutflowSectionCard>
              )}

              {p2p.grns.length > 0 && (
                <OutflowSectionCard>
                  <OutflowSectionTitle>
                    GRNs ({p2p.grns.length})
                  </OutflowSectionTitle>
                  <OutflowChipsRow>
                    {p2p.grns.map((grn) => (
                      <OutflowLinkChip
                        key={grn.id}
                        onClick={() => grn.p2pLink && window.open(grn.p2pLink)}
                        isClickable={Boolean(grn.p2pLink)}
                      >
                        {grn.p2pLink ? <ExternalLinkIcon /> : ""}
                        {grn.id}
                      </OutflowLinkChip>
                    ))}
                  </OutflowChipsRow>
                </OutflowSectionCard>
              )}
            </OutflowContentBox>
          )
        })}
      </DrawerContent>
      {shouldShowReconcileButton(transactionData) ? (
        <DrawerDataRow>
          <ReconcilePrimaryButton
            variant="contained"
            onClick={() => handleReconcileDialogOpen()}
          >
            Reconcile
            <ArrowForwardIcon fontSize="small" sx={{ height: "14px" }} />
          </ReconcilePrimaryButton>
        </DrawerDataRow>
      ) : null}
      {reconcileDialogOpen && (
        <ReconciliationDrawer
          open={reconcileDialogOpen}
          onClose={handleReconcileDialogClose}
          transactionData={transactionData}
        />
      )}
    </StyledTransactionInfo>
  )
}

export default TransactionInfo
